import { useEffect, useState } from "react";
import classNames from "classnames/bind";

import logo from "../../icons/logo64.svg";

import styles from "./PageLoader.module.css";
import { useStateManager } from "utils/useStateManager";

const cx = classNames.bind(styles);

type Props = {
	title?: React.ReactNode;
	description?: React.ReactNode;
	duration: number;
};

const START_OFFSET = 518.363;
const END_OFFSET = 103.6726;

export const PageLoader: React.FC<Props> = ({
	title,
	description,
	duration
}) => {
	const { linkInfo } = useStateManager();
	const [offset, setOffset] = useState<number>(START_OFFSET);

	useEffect(() => {
		let raf: number;

		const animationIteration = (
			time: number,
			duration: number,
			start: number,
			end: number
		) => {
			const diffTime = Math.min(1, (Date.now() - time) / duration);
			const diffOffset = (start - end) * diffTime;
			const newOffset = Math.max(0, start - diffOffset);

			setOffset(newOffset);

			if (newOffset > end) {
				raf = requestAnimationFrame(() =>
					animationIteration(time, duration, start, end)
				);
			}
		};

		animationIteration(Date.now(), duration * 1000, START_OFFSET, END_OFFSET);

		return () => {
			cancelAnimationFrame(raf);
		};
	}, []);

	const content = (
		<div className={cx("container")}>
			<div className={cx("loader")}>
				<div className={cx("icon")}>
					<div className={cx('status')}>
						<svg
							className={cx('svg')}
							width='124'
							height='124'
							viewBox='0 0 124 124'
						>
							<circle
								strokeDashoffset={offset}
								cx="62"
								cy="62"
								r="60"
								fill="none"
								stroke="#DEE3EA"
								strokeWidth="4"
								strokeLinecap="round"
							/>
							<circle
								strokeDashoffset={offset}
								cx="62"
								cy="62"
								r="60"
								fill="none"
								stroke="#26A0D0"
								strokeWidth="4"
								strokeDasharray={START_OFFSET}
								strokeLinecap="round"
							/>
						</svg>
					</div>
					<img className={cx("logo")} src={linkInfo?.custom?.custom_additional_data?.logo || logo} alt="" />
				</div>
				<h2 className={cx("title")}>{title}</h2>
				<div className={cx("text")}>
					<span>{description}</span>
				</div>
			</div>
		</div>
	);

	return content;
};
